import React, { useEffect, useMemo, useState, useCallback } from 'react'
import * as XLSX from 'xlsx'
import { supabase } from '../lib/supabase'
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts'
import { Users, MessageSquare, Bot, UserCheck, TrendingUp, Download, Loader2, CalendarDays } from 'lucide-react'

const TEAL  = '#2E96AB'
const TEAL_L = '#3FB3C9'
const GRANA = '#C1272D'
const ASH   = '#7C828C'

const FILTERS = [
  { key: 'all',      label: 'Unificado' },
  { key: 'whatsapp', label: 'WhatsApp'  },
  { key: 'web',      label: 'Web'       },
]

const tooltipStyle = {
  background: '#11161C', border: '1px solid #1E2630',
  borderRadius: 10, color: '#E6EAEE', fontSize: 12,
}

function ymd(d) { return d.toISOString().slice(0, 10) }

function displayName(c) {
  return c.display_name?.trim() || c.phone?.trim() || c.user_id
}

/* ── KPI Card ─────────────────────────────────────────────────────────────── */
function Kpi({ icon: Icon, label, value, accent }) {
  const isTeal = accent !== 'grana'
  const clr    = isTeal ? 'var(--teal-l)' : 'var(--grana-4)'
  const bg     = isTeal ? 'rgba(46,150,171,.1)'   : 'rgba(193,39,45,.1)'
  const ring   = isTeal ? 'rgba(46,150,171,.2)'   : 'rgba(193,39,45,.2)'
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{
        display: 'inline-flex', width: 36, height: 36,
        alignItems: 'center', justifyContent: 'center',
        borderRadius: 8, background: bg, outline: `1px solid ${ring}`,
        marginBottom: 12,
      }}>
        <Icon size={18} style={{ color: clr }} />
      </div>
      <p className="font-display" style={{ fontSize: 24, fontWeight: 600, color: '#fff' }}>{value}</p>
      <p style={{ marginTop: 2, fontSize: 12, color: ASH }}>{label}</p>
    </div>
  )
}

function ChartTitle({ children }) {
  return (
    <h3 className="font-display" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ash-300)', marginBottom: 16 }}>
      {children}
    </h3>
  )
}

/* ── Página ───────────────────────────────────────────────────────────────── */
export default function Dashboard() {
  const today    = new Date()
  const monthAgo = new Date(today.getTime() - 29 * 86400000)

  const [contacts,  setContacts]  = useState([])
  const [messages,  setMessages]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [exporting, setExporting] = useState(false)
  const [filter,    setFilter]    = useState('all')
  const [from,      setFrom]      = useState(ymd(monthAgo))
  const [to,        setTo]        = useState(ymd(today))

  const load = useCallback(async () => {
    setLoading(true)
    const fromIso = new Date(from + 'T00:00:00').toISOString()
    const toIso   = new Date(to   + 'T23:59:59').toISOString()
    const [{ data: msgs }, { data: cts }] = await Promise.all([
      supabase.from('messages').select('*').gte('time', fromIso).lte('time', toIso).order('time', { ascending: true }).limit(20000),
      supabase.from('contacts').select('*').limit(5000),
    ])
    setMessages(msgs || [])
    setContacts(cts  || [])
    setLoading(false)
  }, [from, to])

  useEffect(() => { load() }, [load])

  /* Filtrado por canal */
  const fMessages = useMemo(() =>
    filter === 'all' ? messages : messages.filter(m => m.channel === filter),
  [messages, filter])

  const fContacts = useMemo(() =>
    filter === 'all' ? contacts : contacts.filter(c => c.channel === filter),
  [contacts, filter])

  /* KPIs */
  const kpis = useMemo(() => {
    const totalMsg   = fMessages.length
    const userMsg    = fMessages.filter(m => m.role === 'user').length
    const botMsg     = fMessages.filter(m => m.role === 'assistant').length
    const activeUsers = new Set(fMessages.map(m => m.user_id)).size
    const todayStr   = ymd(new Date())
    const msgToday   = fMessages.filter(m => m.time.slice(0, 10) === todayStr).length
    const avg        = activeUsers ? (totalMsg / activeUsers).toFixed(1) : '0'
    return { totalMsg, userMsg, botMsg, activeUsers, msgToday, avg, totalContacts: fContacts.length }
  }, [fMessages, fContacts])

  /* Serie diaria */
  const byDay = useMemo(() => {
    const map = new Map()
    const start = new Date(from), end = new Date(to)
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1))
      map.set(ymd(new Date(d)), 0)
    fMessages.forEach(m => {
      const k = m.time.slice(0, 10)
      if (map.has(k)) map.set(k, (map.get(k) || 0) + 1)
    })
    return Array.from(map, ([date, total]) => ({ date: date.slice(5), full: date, total }))
  }, [fMessages, from, to])

  /* Por canal (donut) */
  const byChannel = useMemo(() => [
    { name: 'WhatsApp', value: messages.filter(m => m.channel === 'whatsapp').length, color: TEAL  },
    { name: 'Web',      value: messages.filter(m => m.channel === 'web').length,       color: GRANA },
  ], [messages])

  /* Por hora */
  const byHour = useMemo(() => {
    const arr = Array.from({ length: 24 }, (_, h) => ({ hour: `${h}h`, total: 0 }))
    fMessages.forEach(m => {
      const h   = new Date(m.time).toLocaleString('en-US', { timeZone: 'America/Lima', hour: 'numeric', hour12: false })
      const idx = parseInt(h) % 24
      arr[idx].total++
    })
    return arr
  }, [fMessages])

  /* Top contactos */
  const topContacts = useMemo(() => {
    const map    = new Map()
    fMessages.forEach(m => map.set(m.user_id, (map.get(m.user_id) || 0) + 1))
    const nameOf = new Map(fContacts.map(c => [c.user_id, displayName(c)]))
    return Array.from(map, ([id, total]) => ({ name: (nameOf.get(id) || id).slice(0, 18), total }))
      .sort((a, b) => b.total - a.total).slice(0, 8).reverse()
  }, [fMessages, fContacts])

  /* Exportar Excel */
  async function onExport() {
    setExporting(true)
    try {
      const wb = XLSX.utils.book_new()

      // Resumen
      const resumen = [
        { Indicador: 'Canal/Filtro',               Valor: filter === 'all' ? 'WhatsApp + Web' : filter },
        { Indicador: 'Periodo',                    Valor: `${from} a ${to}` },
        { Indicador: 'Contactos totales',          Valor: kpis.totalContacts },
        { Indicador: 'Usuarios activos (periodo)', Valor: kpis.activeUsers },
        { Indicador: 'Mensajes totales',           Valor: kpis.totalMsg },
        { Indicador: 'Mensajes de clientes',       Valor: kpis.userMsg },
        { Indicador: 'Respuestas del bot',         Valor: kpis.botMsg },
        { Indicador: 'Mensajes hoy',               Valor: kpis.msgToday },
        { Indicador: 'Promedio mensajes/usuario',  Valor: kpis.avg },
        { Indicador: 'Generado el',                Valor: new Date().toLocaleString('es-PE', { timeZone: 'America/Lima' }) },
      ]
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumen), 'Resumen')

      // Mensajes por día
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        byDay.map(d => ({ Fecha: d.full, Mensajes: d.total }))
      ), 'Por día')

      // Contactos
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        fContacts.map(c => ({
          Nombre:   displayName(c),
          Teléfono: c.phone || '',
          Canal:    c.channel === 'whatsapp' ? 'WhatsApp' : 'Web',
          Mensajes: c.message_count || 0,
          'Primer contacto': c.first_seen || '',
          'Último mensaje':  c.last_seen  || '',
        }))
      ), 'Contactos')

      // Mensajes
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
        fMessages.map(m => ({
          Fecha:     m.time,
          Canal:     m.channel === 'whatsapp' ? 'WhatsApp' : 'Web',
          Rol:       m.role === 'user' ? 'Cliente' : 'Bot',
          Contenido: m.content,
        }))
      ), 'Mensajes')

      XLSX.writeFile(wb, `CCIA-Reporte-${ymd(new Date())}.xlsx`)
    } finally { setExporting(false) }
  }

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{ height: '100vh', overflowY: 'auto', padding: '24px 28px' }}>
      <div style={{ maxWidth: 1180, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 24 }}>
          <div>
            <h1 className="font-display" style={{ fontSize: 24, fontWeight: 600, color: '#fff' }}>Dashboard</h1>
            <p style={{ marginTop: 4, fontSize: 13, color: ASH }}>
              Analítica de conversaciones · {FILTERS.find(f => f.key === filter)?.label}
            </p>
          </div>
          <button className="btn btn-primary" onClick={onExport} disabled={exporting || loading}>
            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            Exportar a Excel
          </button>
        </div>

        {/* Controles */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {FILTERS.map(f => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`chip ${filter === f.key ? 'chip-active' : ''}`}>
                {f.label}
              </button>
            ))}
          </div>
          {/* Date picker */}
          <div style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8,
            borderRadius: 8, border: '1px solid var(--ink-700)',
            background: 'var(--ink-900)', padding: '6px 12px',
          }}>
            <CalendarDays size={15} style={{ color: 'var(--ash-600)' }} />
            <input type="date" value={from} max={to}
              onChange={e => setFrom(e.target.value)}
              style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: 'var(--ash-300)', cursor: 'pointer' }} />
            <span style={{ color: 'var(--ash-600)' }}>→</span>
            <input type="date" value={to} min={from} max={ymd(new Date())}
              onChange={e => setTo(e.target.value)}
              style={{ background: 'transparent', border: 'none', outline: 'none', fontSize: 13, color: 'var(--ash-300)', cursor: 'pointer' }} />
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'grid', placeItems: 'center', height: 256, color: 'var(--ash-600)' }}>
            <Loader2 className="animate-spin" size={28} />
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px,1fr))', gap: 16, marginBottom: 24 }}>
              <Kpi icon={Users}        label="Contactos"           value={kpis.totalContacts} accent="teal"  />
              <Kpi icon={UserCheck}    label="Activos (periodo)"   value={kpis.activeUsers}   accent="teal"  />
              <Kpi icon={MessageSquare} label="Mensajes"           value={kpis.totalMsg}      accent="teal"  />
              <Kpi icon={TrendingUp}   label="Mensajes hoy"        value={kpis.msgToday}      accent="grana" />
              <Kpi icon={Bot}          label="Resp. del bot"       value={kpis.botMsg}        accent="teal"  />
              <Kpi icon={MessageSquare} label="Prom. msj/usuario"  value={kpis.avg}           accent="grana" />
            </div>

            {/* Fila 1: AreaChart + Donut */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="panel" style={{ padding: 20 }}>
                <ChartTitle>Mensajes por día</ChartTitle>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={byDay} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor={TEAL} stopOpacity={.5} />
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0}  />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E2630" />
                    <XAxis dataKey="date" tick={{ fill: ASH, fontSize: 11 }} stroke="#1E2630" interval="preserveStartEnd" />
                    <YAxis tick={{ fill: ASH, fontSize: 11 }} stroke="#1E2630" allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#fff' }} />
                    <Area type="monotone" dataKey="total" stroke={TEAL_L} strokeWidth={2} fill="url(#grad1)" name="Mensajes" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="panel" style={{ padding: 20 }}>
                <ChartTitle>Distribución por canal</ChartTitle>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={byChannel} dataKey="value" nameKey="name"
                      cx="50%" cy="50%" innerRadius={58} outerRadius={88} paddingAngle={3}>
                      {byChannel.map((e, i) => <Cell key={i} fill={e.color} stroke="#0E1318" strokeWidth={2} />)}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 8 }}>
                  {byChannel.map(e => (
                    <span key={e.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ash-400)' }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: e.color }} />
                      {e.name} · {e.value}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Fila 2: Por hora + Top contactos */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="panel" style={{ padding: 20 }}>
                <ChartTitle>Actividad por hora (Lima)</ChartTitle>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={byHour} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E2630" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fill: ASH, fontSize: 10 }} stroke="#1E2630" interval={1} />
                    <YAxis tick={{ fill: ASH, fontSize: 11 }} stroke="#1E2630" allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(30,38,48,.5)' }} />
                    <Bar dataKey="total" fill={TEAL} radius={[3,3,0,0]} name="Mensajes" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="panel" style={{ padding: 20 }}>
                <ChartTitle>Top contactos por actividad</ChartTitle>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={topContacts} layout="vertical" margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1E2630" horizontal={false} />
                    <XAxis type="number" tick={{ fill: ASH, fontSize: 11 }} stroke="#1E2630" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fill: ASH, fontSize: 11 }} stroke="#1E2630" width={120} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(30,38,48,.5)' }} />
                    <Bar dataKey="total" fill={GRANA} radius={[0,3,3,0]} name="Mensajes" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
